






























































































































































































import { Vue, Component, Watch, Ref } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import gsap from 'gsap'
import FancyGameSelector from '@/modules/instant/components/FancyGameSelector'
import { getModule } from 'vuex-module-decorators'
import sharedStore from '@/modules/shared/shared.store'
import { TournamentAPI, TournamentEntity } from '@/api/tournament'
import ServerError from '@/utils/server_error'
import { SupportedGame } from '@/api/config'
import InstantMatchListItem from '@/modules/instant/components/InstantMatchListItem'
import { getRealtimeConnection } from '@/utils/realtime'
import { HubConnection } from '@microsoft/signalr'
import { CancellationToken } from '@/utils/cancel'
import MobileGameSelector from '@/modules/instant/components/MobileGameSelector'
import MobilePastMatches from '@/modules/instant/components/MobilePastMatches'
import InstantMatchCatalogLogic from '@/modules/instant/mixins/InstantMatchCatalogLogic'

@Component({
  components: {
    MobileGameSelector,
    InstantMatchListItem,
    FancyGameSelector,
    MobilePastMatches
  }
})
export default class InstantMatchCatalog extends mixins(
  InstantMatchCatalogLogic
) {
  sharedModule = getModule(sharedStore, this.$store)

  @Ref('container')
  containerElement!: HTMLDivElement

  backgroundImage: string = 'none'

  isLoading: boolean = false
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()

  isGameListLoading: boolean = false
  currentGame: string = ''
  currentRegion: string | null = null
  currentRegionMobile: string | null = null
  gameList: SupportedGame[] = []
  tabIndex: number = 0

  @Watch('currentRegionMobile')
  onCurrentRegionMobileChanged() {
    if (!this.$vuetify.breakpoint.mobile) {
      return
    }

    localStorage.setItem(
      'instant_match.current_region',
      this.currentRegionMobile!
    )

    this.fetchGameList(false).then()
  }

  @Watch('currentGame')
  async onCurrentGameChanged(newVal: string, oldVal: string) {
    this.animateBackgroundChange(oldVal === '')
    this.tournamentList = []
    this.page = 1
    await this.fetchAutomatedTournamentCatalog(
      this.page,
      this.currentGame,
      this.currentRegion
    )
  }

  @Watch('currentRegion')
  async onCurrentRegionChanged() {
    this.tournamentList = []
    this.page = 1
    await this.fetchAutomatedTournamentCatalog(
      this.page,
      this.currentGame,
      this.currentRegion
    )
  }

  @Watch('desiredBackgroundImage')
  onDesiredBackgroundImageChanged() {
    if (!this.containerElement) {
      this.backgroundImage = this.desiredBackgroundImage
    }
  }

  animateBackgroundChange(fadeInOnly: boolean) {
    gsap.to(this.containerElement, {
      keyframes: [
        {
          duration: fadeInOnly ? 0 : 0.5,
          backgroundColor: 'rgba(0, 0, 0, 1)',
          onComplete: () => {
            this.backgroundImage = this.desiredBackgroundImage
          }
        },
        {
          duration: 0.5,
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }
      ]
    })
  }

  async onAutomatedTournamentCatalogUpdated() {
    this.tournamentList = []
    this.page = 1
    await this.fetchGameList(!this.currentGame)
    await this.fetchAutomatedTournamentCatalog(
      this.page,
      this.currentGame,
      this.currentRegion
    )
    this.$toast.success(this.$t('InstantMatchCatalog.CatalogUpdated'))
  }

  async created() {
    this.isLoading = true
    this.currentRegion = this.sharedModule.suggestedRegion
    this.currentRegionMobile =
      localStorage.getItem('instant_match.current_region') || this.currentRegion

    // listen signalR events
    this.realtimeConnection = await getRealtimeConnection(
      this.realtimeConnectionCancellationToken
    )
    this.realtimeConnection.on(
      'automatedTournamentCatalogUpdated',
      this.onAutomatedTournamentCatalogUpdated
    )

    try {
      const res = await TournamentAPI.getMemberJoinedAutomatedTournament()
      if (res.data.payload!.isJoined) {
        await this.$router.replace({
          name: 'InstantMatchDetails',
          params: {
            tournamentId: res.data.payload!.tournamentId
          }
        })
      } else {
        this.isLoading = false
        await this.fetchGameList(true)
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('Landing.SystemErrorMessage'))
      }
      console.error(err)
    }

    // Object
    if ('past' in this.$route.query) {
      this.tabIndex = 1
    }
  }

  beforeDestroy() {
    this.realtimeConnectionCancellationToken.cancel()
    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'automatedTournamentCatalogUpdated',
        this.onAutomatedTournamentCatalogUpdated
      )
    }
  }

  async fetchGameList(shouldSetDefaultGame: boolean = false) {
    if (this.isGameListLoading) {
      return
    }

    this.isGameListLoading = true
    try {
      const res = await TournamentAPI.getAutomatedTournamentGameList(
        false,
        this.$vuetify.breakpoint.mobile ? this.currentRegionMobile! : undefined
      )
      this.gameList = res.data.payload as SupportedGame[]
      // this.gameList = this.sharedModule.applicationSupportedGames

      if (shouldSetDefaultGame) {
        // auto select the 2nd game
        const desiredIndex = Math.min(1, this.gameList.length - 1)
        if (this.gameList[desiredIndex]) {
          this.currentGame = this.gameList[desiredIndex].code
        }
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('Landing.SystemErrorMessage'))
      }
      console.error(err)
    } finally {
      this.isGameListLoading = false
    }
  }

  get desiredBackgroundImage() {
    if (this.isLoading) {
      return 'none'
    }

    const game = this.gameList.find(game => game.code === this.currentGame)
    if (!game) return 'none'

    return `url(${game.background})`
  }

  get regionList() {
    return this.sharedModule.applicationRegions
  }

  get regionSelectorList() {
    return [
      {
        value: null,
        text: this.$t('InstantMatchCatalog.AllRegion')
      },
      ...this.sharedModule.applicationRegions.map(region => ({
        value: region.code,
        text: region.name
      }))
    ]
  }

  get numOfPlaceholderItem() {
    return Math.max(1, 4 - this.tournamentList.length)
  }

  async onInstantMatchListScroll(e: MouseEvent) {
    const tournamentListElement = e.target as HTMLDivElement

    if (!tournamentListElement || !this.hasMorePage) {
      return
    }

    const scrollBottom =
      tournamentListElement.scrollHeight - tournamentListElement.scrollTop

    if (scrollBottom < 100) {
      await this.fetchAutomatedTournamentCatalog(
        this.page + 1,
        this.currentGame,
        this.currentRegion
      )
    }
  }
}
